import React, { useEffect, useState, useRef } from "react";
import { localize } from "redux-i18n";
import { connect } from "react-redux";
import { compose } from "redux";
import moment from "moment";
import { Button, SectionComponent, SchemaForm, Loader, View } from "@components";
import actionCreators from "@modules/shared/actionCreator";
import type { State } from "@store/reducers";
import type { PropertiesSchema } from "@types";
import { getAbsenceDetails, getAbsencesSchema } from "@modules/shared/selectors/employee-absences";
import type { EmployeeAbsence } from "@modules/shared/reducer/employee-absences/types";
import type { EmployeeType } from "@modules/employee/types";
import { getEmployee } from "@modules/employee/selectors";
import { selectUserType } from "@modules/account/selectors";
import { USER_TYPES } from "@modules/account/constants";
import { AddEmployeeAbsenceModal } from "@modules/shared/components";
import { getEmployeePositions } from "@modules/shared/selectors";
import type { RoleSummary } from "@modules/employee/reducer/employment-details/types";
import sharedActionCreators from "@modules/shared/actionCreator";
import * as selectors from "@modules/shared/selectors/employee-absences";

type OwnProps = $Exact<{
  clientId: number,
  employeeId: number,
  absenceId: number,
  canEditAbsence?: boolean,
  isReportingSection?: boolean,
  isClientAdmin: boolean,
}>;

type PropsFromState = $Exact<{
  absence: EmployeeAbsence | null,
  absencesSchema: ?PropertiesSchema,
  employee: EmployeeType,
  userType: string,
  employeePositions: RoleSummary[],
  absenceShiftPattern: Array<Object>,
}>;

type PropsFromDispatch = $Exact<{
  getEmployeeAbsence: typeof actionCreators.GetEmployeeAbsenceById,
  updateEmployeeAbsence: typeof actionCreators.UpdateEmployeeAbsence,
  getEmployeeAbsencesSchema: typeof actionCreators.GetEmployeeAbsencesSchema,
  getEmployeePositions: typeof sharedActionCreators.GetSharedEmployeePositions,
  getEmployeeBankHolidays: typeof sharedActionCreators.GetEmployeeBankHolidaysOptions,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

function AbsenceDetailsInline(props: Props) {
  const t = props.t;
  const formRef = useRef(null);
  const [editMode, setEditMode] = useState(false);
  const [isSectionOpen, setIsSectionOpen] = useState(true);
  const [formKey, setFormKey] = useState(Date.now());

  const [formData, setFormData] = useState(null);
  const schema = props.absencesSchema && {
    ...props.absencesSchema,
    properties: {
      ...props.absencesSchema.properties,
      id: {
        type: "integer",
        title: props.t("ABSENCE_REASONS_ID"),
      },
      alertsTriggered: {
        type: "integer",
        title: props.t("ALERTS_TRIGGERED"),
      },
      createdAt: {
        format: "date-time",
        title: props.t("DATE_REPORTED"),
        type: "string",
      },
      closeAt: {
        format: "date-time",
        title: props.t("DATE_CLOSED"),
        type: "string",
      },
    },
  };

  useEffect(() => {
    if (
      schema &&
      schema.hasOwnProperty("dependencies") &&
      formData &&
      !formData?.absenceReason?.id
    ) {
      // Injecting another option for the dropdowns but only if there is missing absenceReason.id
      const consentRecord = {
        const: -1,
        title: props.t("CONSENT_WITHHELD"),
      };
      const schemaDeps = schema.dependencies.absenceType.oneOf;
      const injectRecord = arr => {
        const alreadyExists = arr.some(opt => opt.const === -1);
        if (!alreadyExists) {
          arr.unshift(consentRecord);
          setFormKey(Date.now());
        }
      };

      if (
        schemaDeps[0].properties.absenceReason.properties.id.oneOf &&
        schemaDeps[1].properties.absenceReason.properties.id.oneOf
      ) {
        injectRecord(schemaDeps[0].properties.absenceReason.properties.id.oneOf);
        injectRecord(schemaDeps[1].properties.absenceReason.properties.id.oneOf);
      }
    }
  }, [formData?.absenceReason?.id, schema?.dependencies?.absenceType?.oneOf]);

  const uischema = generateUiSchema({
    canAccessConsent:
      props.userType === USER_TYPES.SERVICE_MANAGER || props.userType === USER_TYPES.ADMIN,
  });

  const absence = props.absence;
  useEffect(() => {
    if (props.absenceId) {
      props.getEmployeeAbsence({
        clientId: props.clientId,
        employeeId: props.employeeId,
        absenceId: props.absenceId,
        isReportingRequest: props.isReportingSection,
      });

      props.getEmployeePositions({
        clientId: props.clientId,
        employeeId: props.employeeId,
      });
      props.getEmployeeBankHolidays({
        clientId: props.clientId,
        employeeId: props.employeeId,
      });
    }

    if (!props.absencesSchema) {
      props.getEmployeeAbsencesSchema({
        clientId: props.clientId,
        employeeId: props.employeeId,
      });
    }
  }, []);

  useEffect(() => {
    setFormData(absence);
  }, [absence]);

  function toggleSection() {
    setIsSectionOpen(!isSectionOpen);
  }

  function submitForm() {
    formRef.current.submit();
  }

  function cancelEdit() {
    setFormData(props.absence);
    setEditMode(false);
  }

  function toggleEdit() {
    setEditMode(!editMode);
  }

  function handleChange(data) {
    setFormData(fixRTWDateIfAfterFitDate(data));
    // setFormData(data);
  }

  function fixRTWDateIfAfterFitDate(data) {
    let startsAt = moment(data.startsAt);
    let fitAt = moment(data.fitAt);
    let rtwAt = moment(data.rtwAt);

    if (moment(startsAt).isAfter(fitAt)) {
      fitAt = moment(startsAt).add(1, "minutes");
    }
    if (moment(fitAt).isAfter(rtwAt)) {
      rtwAt = moment(fitAt).add(1, "minutes");
    }

    const d = {
      ...data,
      fitAt: fitAt.toISOString(),
      rtwAt: rtwAt.toISOString(),
    };
    setFormData(d);

    return d;
  }

  function submitHandler(formData: Object) {
    props.updateEmployeeAbsence({
      clientId: props.clientId,
      employeeId: props.employeeId,
      formData: formData.absence ? formData.absence : formData,
    });
  }

  function allowedToEdit() {
    const { absence, canEditAbsence } = props;
    let canEdit = !!canEditAbsence;

    if (!absence) return false;

    if (canEdit) {
      const userTypes =
        [USER_TYPES.CLIENT_ADMIN, USER_TYPES.MANAGER, USER_TYPES.FULL_HR_ACCESS].indexOf(
          props.userType,
        ) > -1;

      // client admin and no consent to share
      if (!!userTypes && !(props.absence || {}).absenceReason?.id) {
        canEdit = false;
      }
    }

    return canEdit;
  }

  function isValid() {
    return true;
  }

  function generateUiSchema({ canAccessConsent }) {
    if (!props.employee || !formData) return {};
    const datePickerOptions = {
      enableTime: true,
      dateFormat: "DD/MM/YYYY HH:mm",
      placeholder: "dd/mm/yyyy hh:mm",
    };
    return {
      id: {
        "ui:options": {
          disabled: true,
        },
      },
      isHistoricAbsence: { "ui:widget": "hidden" },
      createdAt: {
        "ui:widget": "date-picker",
        "ui:options": {
          ...datePickerOptions,
          disabled: true,
        },
      },
      closeAt: {
        "ui:widget": "date-picker",
        "ui:options": {
          ...datePickerOptions,
          disabled: true,
        },
      },

      startsAt: {
        "ui:widget": "date-picker",
        "ui:options": {
          ...datePickerOptions,
          minDate: moment(props.employee.joinDate)
            .add(1, "minutes")
            .toISOString(),
        },
        "ui:title": t("START_DATE"),
      },

      fitAt: {
        "ui:widget": "date-picker",
        "ui:options": {
          ...datePickerOptions,
          minDate: moment(formData.startsAt)
            .add(1, "minutes")
            .toISOString(),
        },
        "ui:title": t("FIT_DATE"),
      },
      rtwAt: {
        "ui:widget": "date-picker",
        "ui:options": {
          ...datePickerOptions,
          minDate: moment(formData.fitAt).toISOString(),
        },
        "ui:title": t("RTW_DATE"),
      },

      hasConsentToShare: {
        "ui:widget": canAccessConsent ? "toggle" : "hidden",
        "ui:options": {
          label: t("CONSENT_GIVEN"),
        },
      },
      isWorkRelated: {
        "ui:widget": "toggle",
        "ui:options": {
          label: t("WORK_RELATED_HEADER"),
          colStart: 3,
          rowStart: canAccessConsent ? 4 : 3,
        },
      },
      isAccidentRelated: {
        "ui:widget": "toggle",
        "ui:options": {
          label: t("IS_ACCIDENTS_RELATED"),
          colStart: 3,
          rowStart: canAccessConsent ? 5 : 4,
        },
      },
      absenceType: {
        "ui:options": {
          label: t("TYPE"),
        },
      },
      alertsTriggered: {
        "ui:options": {
          disabled: true,
          colStart: 1,
          rowStart: 5,
          className: "",
        },
      },
      "ui:order": [
        "id",
        "startsAt",
        "absenceType",
        "status",
        "fitAt",
        "absenceReason",
        "reportedAt",
        "createdAt",
        "rtwAt",
        "hasConsentToShare",
        "*",
      ],
    };
  }

  function modifySchema() {
    let _schema = JSON.parse(JSON.stringify(schema));

    // show the Canceled status and the cancel reason if any
    _schema.properties.status.oneOf.push({
      title: formData?.masterAbsenceCancelReason?.name
        ? `${props.t("CANCELLED")} - ${formData?.masterAbsenceCancelReason?.name}`
        : props.t("CANCELLED"),
      const: "CANCELLED",
    });

    // if the absence reason exists but has been disabled, it will be missing into an absence reasons list
    //  to be displayed the absence reason needs to be added to the schema options.
    if (
      _schema &&
      schema.hasOwnProperty("dependencies") &&
      formData &&
      formData?.absenceReason?.id
    ) {
      _schema.dependencies.absenceType.oneOf.map(i => {
        // detect a correct absence type list
        if (i.properties.absenceType.const === formData?.absenceType) {
          const reasonsList = [...(i.properties.absenceReason.properties.id?.oneOf || [])];

          // check if missing in the reason list
          const existReason = reasonsList.find(i => i.const === formData.absenceReason.id);

          // add the formData absence reason to the list
          if (!!existReason === false) {
            reasonsList.unshift({
              const: formData.absenceReason.id,
              title: formData.absenceReason.name,
            });

            i.properties.absenceReason.properties.id.oneOf = reasonsList;

            return i;
          }

          return i;
        }

        return i;
      });
    }

    return _schema;
  }

  if (!formData) return <Loader />;
  return (
    <>
      <SectionComponent
        className="gs-container"
        wrapperClassName="flex-column"
        title={<h5>{props.t("DETAILS")}</h5>}
        collapsable
        isOpen={isSectionOpen}
        onTitleClick={toggleSection}
        rightLink={
          <View display={allowedToEdit() ? "flex" : "none"}>
            {!editMode ? (
              <Button materialIcon="edit" onClick={toggleEdit}>
                {props.t("EDIT")}
              </Button>
            ) : (
              <div className="d-flex">
                <Button color="secondary" onClick={cancelEdit}>
                  {props.t("CANCEL")}
                </Button>
                <Button
                  materialIcon="replay_circle_filled"
                  className="ml-2"
                  onClick={submitForm}
                  disabled={!isValid()}
                >
                  {props.t("SUBMIT")}
                </Button>
              </div>
            )}
          </View>
        }
      >
        {schema && formData && (
          <SchemaForm
            ref={formRef}
            key={formKey}
            schema={modifySchema()}
            onSubmit={submitHandler}
            onChange={handleChange}
            uiSchema={uischema}
            formData={formData}
            readonly={true}
            externalSubmit
            columns={3}
          />
        )}
      </SectionComponent>
      {editMode && props.absencesSchema && (
        <AddEmployeeAbsenceModal
          onDismissHandler={cancelEdit}
          onChange={handleChange}
          onSubmit={submitHandler}
          schema={props.absencesSchema}
          absence={props.absence}
          timeLostShiftPattern={props.absenceShiftPattern}
          absenceId={props.absence?.id}
          employee={props.employee}
          clientId={props.clientId}
          employeePositions={props.employeePositions}
        />
      )}
    </>
  );
}

const mapStateToProps = (state: State, ownProps: OwnProps): PropsFromState => ({
  absence: getAbsenceDetails(state),
  absencesSchema: getAbsencesSchema(state),
  employee: getEmployee(ownProps.employeeId)(state),
  userType: selectUserType(state),
  employeePositions: getEmployeePositions(state),
  absenceShiftPattern: selectors.getAbsenceShiftPattern(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getEmployeeAbsence: actionCreators.GetEmployeeAbsenceById,
  updateEmployeeAbsence: actionCreators.UpdateEmployeeAbsence,
  getEmployeeAbsencesSchema: actionCreators.GetEmployeeAbsencesSchema,
  getEmployeePositions: sharedActionCreators.GetSharedEmployeePositions,
  getEmployeeBankHolidays: sharedActionCreators.GetEmployeeBankHolidaysOptions,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(AbsenceDetailsInline);
